import { useIsUngated } from 'portals-platform-lib';
import { AutoHeightSlide } from './AutoHeightSlide';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { CommentsIcon } from 'foundations-assets/icons/components/CommentsIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { DEFAULT_FILTER_VALUES, getDateFilterLabel } from '../constants/filters';
import { GATES } from '../constants/gates';
import { UserTicketStatusOption } from '../data/types';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { useConfiguredColumnLabelOverride } from '../utils/useConfiguredColumnLabelOverride';
import { usageTracker } from '../utils/usageTracker';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SCREEN = 'ticket-listings';
const getStatusLabel = status => {
  switch (status) {
    case UserTicketStatusOption.OPEN:
      return I18n.text('customer-portal-app.filter.open');
    case UserTicketStatusOption.CLOSED:
      return I18n.text('customer-portal-app.filter.closed');
    default:
      return I18n.text('customer-portal-app.filter.all');
  }
};
const FilterPill = ({
  label,
  value,
  icon,
  use = 'default',
  onClose,
  testId
}) => {
  const isWarning = use === 'warning';
  return /*#__PURE__*/_jsx(Tag, {
    use: use
    // @ts-expect-error - Tag types don't expose `color`, but Trellis accepts it as a token override on the styled wrapper; needed to drive the warning pill's icon + label color in both light and dark mode.
    ,
    color: isWarning ? 'fill-accent-yellow-pressed' : undefined,
    closeable: true,
    onCloseClick: onClose,
    "data-test-id": testId,
    marginInlineStart: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      marginInlineEnd: "200",
      as: "span",
      children: [icon, isWarning ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs("span", {
          children: [label, ":"]
        }), /*#__PURE__*/_jsx("strong", {
          style: {
            color: '#141414'
          },
          children: value
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Text, {
          as: "span",
          color: "text-core-subtle",
          children: [label, ":"]
        }), /*#__PURE__*/_jsx("strong", {
          children: value
        })]
      })]
    })
  });
};
export function ActiveFilterPills() {
  var _useConfiguredColumnL, _useConfiguredColumnL2, _useConfiguredColumnL3;
  const isAdvancedSearchUngated = useIsUngated(GATES.ADVANCED_SEARCH);
  const {
    statusFilter,
    setStatusFilter,
    createDateFilter,
    setCreateDateFilter,
    lastActivityFilter,
    setLastActivityFilter,
    conversationSearch,
    setConversationSearch,
    resetFilters,
    activeFilterCount
  } = useTicketListingsSortAndFilters();
  const statusLabel = (_useConfiguredColumnL = useConfiguredColumnLabelOverride('status')) !== null && _useConfiguredColumnL !== void 0 ? _useConfiguredColumnL : I18n.text('customer-portal-app.filters.ticketStatus');
  const createdateLabel = (_useConfiguredColumnL2 = useConfiguredColumnLabelOverride('createdate')) !== null && _useConfiguredColumnL2 !== void 0 ? _useConfiguredColumnL2 : I18n.text('customer-portal-app.property.createdate');
  const lastActivityLabel = (_useConfiguredColumnL3 = useConfiguredColumnLabelOverride('hs_lastactivitydate')) !== null && _useConfiguredColumnL3 !== void 0 ? _useConfiguredColumnL3 : I18n.text('customer-portal-app.ticketListings.tableHeader.lastActivity');
  const isExpanded = activeFilterCount > 0;
  const trackRemoval = filterName => {
    usageTracker.track('Customer Portal App', 'Tickets Discovered', {
      action: 'active-filter-removed',
      screen: SCREEN,
      filter: filterName
    });
  };
  return /*#__PURE__*/_jsx(AutoHeightSlide, {
    expanded: isExpanded,
    durationMs: 220,
    exitDurationMs: 150,
    "data-test-id": "active-filter-pills-container",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexWrap: "wrap",
      alignItems: "center",
      gap: "200",
      paddingBlockEnd: "400",
      "data-test-id": "active-filter-pills",
      children: [isAdvancedSearchUngated && conversationSearch !== '' && /*#__PURE__*/_jsx(FilterPill, {
        testId: "active-filter-conversation-search",
        use: "warning",
        icon: /*#__PURE__*/_jsx(CommentsIcon, {
          size: "300"
        }),
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.activeFilters.inConversation"
        }),
        value: `"${conversationSearch}"`,
        onClose: () => {
          trackRemoval('conversationSearch');
          setConversationSearch('');
        }
      }), statusFilter !== DEFAULT_FILTER_VALUES.statusFilter && /*#__PURE__*/_jsx(FilterPill, {
        testId: "active-filter-status",
        label: statusLabel,
        value: getStatusLabel(statusFilter),
        onClose: () => {
          trackRemoval('status');
          setStatusFilter(DEFAULT_FILTER_VALUES.statusFilter);
        }
      }), createDateFilter !== DEFAULT_FILTER_VALUES.createDateFilter && /*#__PURE__*/_jsx(FilterPill, {
        testId: "active-filter-createdate",
        label: createdateLabel,
        value: getDateFilterLabel(createDateFilter),
        onClose: () => {
          trackRemoval('createDateFilter');
          setCreateDateFilter(DEFAULT_FILTER_VALUES.createDateFilter);
        }
      }), lastActivityFilter !== DEFAULT_FILTER_VALUES.lastActivityFilter && /*#__PURE__*/_jsx(FilterPill, {
        testId: "active-filter-last-activity",
        label: lastActivityLabel,
        value: getDateFilterLabel(lastActivityFilter),
        onClose: () => {
          trackRemoval('lastActivityFilter');
          setLastActivityFilter(DEFAULT_FILTER_VALUES.lastActivityFilter);
        }
      }), activeFilterCount > 1 && /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        size: "extra-small",
        "data-test-id": "active-filters-clear-all",
        onClick: () => {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-filter-cleared',
            screen: SCREEN
          });
          resetFilters();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.activeFilters.clearAll"
        })
      })]
    })
  });
}